import React, { useEffect, useState, useMemo } from "react";
import api from 'state/axios';
import toast from "react-hot-toast";
import {
    Box,
    Select,
    MenuItem,
    Button,
    TextField,
    Stack,
    Divider,
} from "@mui/material";
import { format } from "date-fns";
import { getDatesBetween } from "state/helpers";
import ConfirmDialog from "components/ConfirmDialog";


const BottomLeftPanel = ({
  startDate,
  endDate,
  onOptimisticCreate,
  onOptimisticCommit,
  onOptimisticRollback,
  canCreate = false,
  canDelete = false,
  onDeleted,
}) => {

    const [shifts, setShifts] = useState([]);
    const [shiftId, setShiftId] = useState("");
    const [requiredEmployees, setRequiredEmployees] = useState(1);
    const [confirmOpen, setConfirmOpen] = useState(false);


    /* ---------- LOAD SHIFTS ---------- */
    useEffect(() => {
        api.get('/shifts?active=true')
            .then((res) => setShifts(res.data || []))
            .catch(() => toast.error("Load shifts failed!"));
    }, []);

    const shiftMap = useMemo(() => {
        const map = new Map();
        shifts.forEach(s => map.set(s._id, s));
        return map;
    }, [shifts]);

    /* ---------- CREATE ---------- */
    const handleCreate = async () => {
        if (!shiftId || !startDate || !endDate) {
            toast.error("Please select shift & date range.");
            return;
        }
        const tempPrefix = `temp-${Date.now()}`;

        try {
            const shift = shiftMap.get(shiftId);
            if (!shift.startTime || !shift.endTime) {
                toast.error("Invalid shift time!");
                return;
            }
            const dates = getDatesBetween(startDate, endDate);

            const resolvedRequired = requiredEmployees || shift.requiredEmployees || 1;
            const optimisticAssignments = dates.map((date) => ({
                _id: `${tempPrefix}-${date}`,
                shiftId: {
                    _id: shift._id,
                    shiftCode: shift.shiftCode,
                    shiftName: shift.shiftName,
                    startTime: shift.startTime,
                    endTime: shift.endTime,
                    crossesDay: shift.crossesDay,
                    requiredEmployees: resolvedRequired,
                    requiredSkills: shift.requiredSkills,
                    paidFactor: shift.paidFactor,
                    workMode: shift.workMode,
                    isActive: shift.isActive,
                    color: shift.color,
                },
                date,
                requiredEmployees: resolvedRequired,
                assignees: [],
                memo: "",
                source: "AUTO",
                isLocked: false,
                __optimistic: true,
            }));
            onOptimisticCreate?.(optimisticAssignments);

            const res = await api.post('/assignments/bulk-create', {
                shiftId,
                startDate,
                endDate,
                requiredEmployees: requiredEmployees || undefined,
            });

            onOptimisticCommit?.(tempPrefix, res.data?.unassignments || []);
            toast.success(res.data?.message || "Assignments created.");
        } catch (err) {
            onOptimisticRollback?.(tempPrefix);
            toast.error(err?.response?.data?.message || "Failed to create assignments!");
        }
    };

    /* ---------- DELETE BY PERIOD ---------- */
    const handleDeleteConfirm = async () => {
        try {
            const res = await api.delete('/assignments/delete-by-period', {
                data: { startDate, endDate, shiftId: shiftId || undefined },
            });
            toast.success(res.data?.message || "Assignments deleted.");
            onDeleted?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Delete failed.");
        }
    };

    const fmtDate = (d) => d ? format(new Date(d), 'dd/MM/yyyy') : '-';
    const selectedShiftName = shiftId
        ? (shiftMap.get(shiftId)?.shiftName || shiftMap.get(shiftId)?.shiftCode)
        : null;

    return (
        <Box sx={{ height: "100%", overflow: "hidden", p: 1 }}>
            <Stack spacing={1.5}>
                <Select
                    size="small"
                    value={shiftId}
                    onChange={(e) => {
                        const id = e.target.value;
                        setShiftId(id);
                        const shift = shiftMap.get(id);
                        if (shift?.requiredEmployees) setRequiredEmployees(shift.requiredEmployees);
                    }}
                    displayEmpty
                >
                    <MenuItem value="">Select shift</MenuItem>
                    {shifts.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                            {s.shiftName || s.shiftCode}
                        </MenuItem>
                    ))}
                </Select>

                <TextField
                    size="small"
                    variant="outlined"
                    type="number"
                    label="Required employees"
                    value={requiredEmployees || 1}
                    onChange={(e) => setRequiredEmployees(e.target.value)}
                    slotProps={{
                        input: { inputProps: { min: 1 } },
                    }}
                />

                <Button
                    size="small"
                    variant="contained"
                    disabled={!shiftId || !canCreate}
                    onClick={handleCreate}
                >
                    Create
                </Button>

                <Divider />

                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={!canDelete || !startDate || !endDate}
                    onClick={() => setConfirmOpen(true)}
                >
                    Delete
                </Button>
            </Stack>

            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Confirm?"
                message={`Delete all assignments from ${fmtDate(startDate)} to ${fmtDate(endDate)}`
                    + (selectedShiftName ? ` - shift ${selectedShiftName}` : '') + '?'}
                confirmText="Delete"
                confirmColor="error"
            />
        </Box>
    );
};

export default React.memo(BottomLeftPanel);
